import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Archive, ArrowCounterClockwise, MagnifyingGlass, Trash, ArrowLeft, ArrowRight, Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArchivedRecordsPage = () => {
  const [records, setRecords] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStructure, setSelectedStructure] = useState('');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    axios.get(`${API_URL}/api/data-structures`).then(res => {
      setStructures(res.data);
    }).catch(() => {});
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedStructure) params.append('structure_id', selectedStructure);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('limit', 20);

      const { data } = await axios.get(`${API_URL}/api/records/archived?${params}`);
      setRecords(data.records);
      setTotalPages(data.pages);
    } catch (e) {
      toast.error('Failed to fetch archived records');
    } finally {
      setLoading(false);
    }
  }, [selectedStructure, searchTerm, page]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleUnarchive = async (recordId) => {
    try {
      await axios.post(`${API_URL}/api/records/${recordId}/unarchive`);
      toast.success('Record restored');
      fetchRecords();
    } catch (e) {
      toast.error('Failed to restore record');
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Permanently delete this record? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/records/${recordId}`);
      toast.success('Record permanently deleted');
      fetchRecords();
    } catch (e) {
      toast.error('Failed to delete record');
    }
  };

  const handleBulkUnarchive = async () => {
    if (selectedRecords.length === 0) return;
    try {
      await axios.post(`${API_URL}/api/records/bulk-unarchive`, selectedRecords);
      toast.success(`Restored ${selectedRecords.length} records`);
      setSelectedRecords([]);
      fetchRecords();
    } catch (e) {
      toast.error('Failed to restore records');
    }
  };

  const toggleRecordSelection = (recordId) => {
    setSelectedRecords(prev =>
      prev.includes(recordId) ? prev.filter(id => id !== recordId) : [...prev, recordId]
    );
  };

  const toggleAllRecords = () => {
    if (selectedRecords.length === records.length && records.length > 0) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map(r => r.id));
    }
  };

  const getStructureName = (id) => structures.find(s => s.id === id)?.name || '-';

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="archived-page-title">Archived Records</h1>
          <p className="text-muted-foreground">View and restore archived records</p>
        </div>
        <div className="flex gap-2">
          {selectedRecords.length > 0 && (
            <Button variant="outline" onClick={handleBulkUnarchive} data-testid="bulk-unarchive-btn">
              <ArrowCounterClockwise size={18} className="mr-1" />
              Restore {selectedRecords.length}
            </Button>
          )}
          <Link to="/records">
            <Button variant="outline" data-testid="back-to-records-btn">
              <ArrowLeft size={18} className="mr-1" /> Back to Records
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search archived records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-archived"
                />
              </div>
            </div>
            <div className="w-48">
              <Label className="text-xs text-muted-foreground mb-1 block">Schema</Label>
              <Select value={selectedStructure || "all"} onValueChange={(v) => { setSelectedStructure(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="filter-structure">
                  <SelectValue placeholder="All schemas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All schemas</SelectItem>
                  {structures.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedRecords.length === records.length && records.length > 0}
                    onCheckedChange={toggleAllRecords}
                    data-testid="select-all-archived"
                  />
                </TableHead>
                <TableHead>Record</TableHead>
                <TableHead>Schema</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Archived At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="spinner w-6 h-6 mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : records.length > 0 ? (
                records.map(record => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRecords.includes(record.id)}
                        onCheckedChange={() => toggleRecordSelection(record.id)}
                        data-testid={`select-archived-${record.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">
                        {record.data?.customer_name || record.data?.name || `Record ${record.id.slice(0, 8)}`}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {record.data?.policy_number || record.data?.email || ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{getStructureName(record.structure_id)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{record.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {record.archived_at ? new Date(record.archived_at).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/records/${record.id}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`view-archived-${record.id}`}>
                            <Eye size={16} />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-600 hover:text-green-700"
                          onClick={() => handleUnarchive(record.id)}
                          title="Restore"
                          data-testid={`unarchive-${record.id}`}
                        >
                          <ArrowCounterClockwise size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(record.id)}
                          title="Delete permanently"
                          data-testid={`delete-archived-${record.id}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Archive size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-muted-foreground">No archived records</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ArrowLeft size={16} className="mr-1" /> Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              Next <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchivedRecordsPage;
